import React from 'react';

import styled from 'styled-components';

import ScoreBar from 'components/AiCompetitiveIntelligence/ScoreBar';
import { getOrdinalSuffix } from 'components/View/utils';
import { AICompetitiveIntelligenceRelatedDataType } from 'types/recommendations';

const CategoryColumn = styled.div`
  width: 420px;
  border-right: solid 1px var(--line-gray);
  padding-right: 20px;
  padding-bottom: 30px;
`;

const ColumnHeaderRow = styled.div`
  display: flex;
  flex-flow: row;
  gap: 10px;
  font-size: 10px;
  color: var(--text-secondary);
  padding-top: 20px;
  padding-bottom: 8px;

  span:first-child {
    flex: 1;
  }

  /* Matches CategoryScoreText width (56px) + gap (10px) + CategoryScoreBar width (100px). */
  span:nth-child(2) {
    width: 166px;
  }

  /* Matches CategoryRank width + alignment. */
  span:last-child {
    width: 40px;
    flex-shrink: 0;
    text-align: right;
  }
`;

const CategoryRow = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: solid 1px var(--line-gray);

  &:last-child {
    border-bottom: none;
  }
`;

const CategoryLabel = styled.div`
  flex: 1;
  font-size: 14px;
`;

const CategoryScoreBar = styled.div`
  width: 100px;
`;

const CategoryScoreText = styled.span`
  display: inline-block;
  width: 56px;
  flex-shrink: 0;
  font-size: 12px;
`;

const CategoryScoreValue = styled.span`
  font-size: 16px;
`;

const CategoryRank = styled.div`
  width: 40px;
  flex-shrink: 0;
  font-size: 12px;
  text-align: right;
`;

const EmptyMessage = styled.div`
  color: var(--text-secondary);
  font-size: 12px;
  padding: 16px 0;
`;

type Props = {
  categories: AICompetitiveIntelligenceRelatedDataType[];
};

const CategoryRankList = ({ categories }: Props) => {
  return (
    <CategoryColumn data-test-id="category-breakdown-rank-list">
      <ColumnHeaderRow>
        <span>CATEGORY RANK</span>
        <span>SCORE</span>
        <span>RANK</span>
      </ColumnHeaderRow>
      {categories.length === 0 && (
        <EmptyMessage data-test-id="category-breakdown-rank-list-empty">No data yet.</EmptyMessage>
      )}
      {categories.map((category) => {
        const score = category.score ?? 0;
        const isLeading = category.ranking === 1;

        return (
          <CategoryRow key={category.config_id} data-test-id={`category-breakdown-rank-row-${category.config_id}`}>
            <CategoryLabel>{category.display_label}</CategoryLabel>
            <CategoryScoreText>
              <CategoryScoreValue>{score}</CategoryScoreValue>/100
            </CategoryScoreText>
            <CategoryScoreBar>
              <ScoreBar score={score} positive={isLeading} thin />
            </CategoryScoreBar>
            <CategoryRank>{category.ranking ? getOrdinalSuffix(category.ranking) : '-'}</CategoryRank>
          </CategoryRow>
        );
      })}
    </CategoryColumn>
  );
};

export default CategoryRankList;
